/**
 * Image compression and processing utilities using browser-image-compression
 */
import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number; // 0.1 to 1.0
    format?: 'jpeg' | 'png' | 'webp';
    maintainAspectRatio?: boolean;
}

export interface CompressionResult {
    file: File;
    originalSize: number;
    compressedSize: number;
    compressionRatio: number;
    dimensions: {
        width: number;
        height: number;
    };
}

const DEFAULT_COMPRESSION_OPTIONS: Required<CompressionOptions> = {
    maxWidth: 1920,
    maxHeight: 1920,
    quality: 0.8,
    format: 'jpeg',
    maintainAspectRatio: true,
};

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
export function calculateDimensions(
    originalWidth: number,
    originalHeight: number,
    maxWidth: number,
    maxHeight: number,
    maintainAspectRatio: boolean = true
): { width: number; height: number } {
    if (!maintainAspectRatio) {
        return { width: maxWidth, height: maxHeight };
    }

    const aspectRatio = originalWidth / originalHeight;

    let newWidth = originalWidth;
    let newHeight = originalHeight;

    // Scale down if image is larger than max dimensions
    if (originalWidth > maxWidth) {
        newWidth = maxWidth;
        newHeight = newWidth / aspectRatio;
    }

    if (newHeight > maxHeight) {
        newHeight = maxHeight;
        newWidth = newHeight * aspectRatio;
    }

    return {
        width: Math.round(newWidth),
        height: Math.round(newHeight),
    };
}

/**
 * Compress image file using browser-image-compression library
 */
export async function compressImage(
    file: File,
    options: CompressionOptions = {}
): Promise<CompressionResult> {
    const opts = { ...DEFAULT_COMPRESSION_OPTIONS, ...options };

    try {
        // Get original dimensions (for future use)
        // const originalDimensions = await getImageDimensions(file);

        // Configure browser-image-compression options
        const compressionOptions = {
            maxSizeMB: 2, // Maximum file size in MB
            maxWidthOrHeight: Math.max(opts.maxWidth, opts.maxHeight),
            useWebWorker: true, // Use web worker for better performance
            quality: opts.quality,
            fileType: opts.format === 'jpeg' ? 'image/jpeg' : 
                     opts.format === 'png' ? 'image/png' : 
                     opts.format === 'webp' ? 'image/webp' : 
                     file.type, // Keep original format if not specified
            initialQuality: opts.quality,
            alwaysKeepResolution: !opts.maintainAspectRatio,
        };

        // Compress the image
        const compressedFile = await imageCompression(file, compressionOptions);

        // Get compressed dimensions
        const compressedDimensions = await getImageDimensions(compressedFile);

        // Calculate compression ratio
        const compressionRatio = file.size > 0 ? (file.size - compressedFile.size) / file.size : 0;

        return {
            file: compressedFile,
            originalSize: file.size,
            compressedSize: compressedFile.size,
            compressionRatio: Math.max(0, compressionRatio), // Ensure non-negative
            dimensions: compressedDimensions,
        };
    } catch (error) {
        throw new Error(`Image compression failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

/**
 * Get image dimensions without loading the full image
 */
export function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
            });
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image for dimension calculation'));
        };

        img.src = url;
    });
}

/**
 * Check if image needs compression based on size and dimensions
 */
export async function shouldCompressImage(
    file: File,
    options: CompressionOptions = {}
): Promise<boolean> {
    const opts = { ...DEFAULT_COMPRESSION_OPTIONS, ...options };

    // Check file size (compress if larger than 2MB)
    const maxFileSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxFileSize) {
        return true;
    }

    try {
        // Check dimensions
        const { width, height } = await getImageDimensions(file);
        return width > opts.maxWidth || height > opts.maxHeight;
    } catch {
        // If we can't get dimensions, compress to be safe
        return true;
    }
}

/**
 * Smart compression that only compresses when necessary
 */
export async function smartCompress(
    file: File,
    options: CompressionOptions = {}
): Promise<CompressionResult> {
    const needsCompression = await shouldCompressImage(file, options);

    if (!needsCompression) {
        // Return original file with metadata
        const dimensions = await getImageDimensions(file);
        return {
            file,
            originalSize: file.size,
            compressedSize: file.size,
            compressionRatio: 0,
            dimensions,
        };
    }

    return compressImage(file, options);
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format compression ratio as percentage
 */
export function formatCompressionRatio(ratio: number): string {
    return `${Math.round(ratio * 100)}%`;
}